import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Alert, AlertTitle, Box, Button, Card, CardContent, Checkbox, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl,
  FormControlLabel, IconButton, InputLabel, ListItemText, MenuItem, Paper, Select, Stack,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../services/api';
import { formatCreated } from '../utils/formatCreated';

/**
 * API keys and outbound webhooks.
 *
 * Both were built as an API and had no interface at all, which made them
 * effectively absent: an administrator could not discover they existed, and a
 * key that can only be minted with curl will be minted rarely and stored
 * badly.
 */

const fmt = (v) => (v ? new Date(v).toLocaleString() : '—');

function ApiKeys({ agents, onError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', scope: 'read', agent: '' });
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/integrations/api-keys/');
      setRows(r.data.api_keys || []);
    } catch (e) {
      onError(e.response?.data?.detail || 'Could not load API keys.');
    } finally { setLoading(false); }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const body = { name: form.name, scope: form.scope };
      if (form.agent) body.agent = form.agent;
      const r = await api.post('/integrations/api-keys/', body);
      // Shown once and never again — hold it in state until dismissed.
      setIssued(r.data);
      setOpen(false);
      setForm({ name: '', scope: 'read', agent: '' });
      load();
    } catch (e) {
      onError(e.response?.data?.detail || JSON.stringify(e.response?.data || 'Could not create the key.'));
    } finally { setBusy(false); }
  };

  const revoke = async (id) => {
    try { await api.post(`/integrations/api-keys/${id}/revoke/`); load(); }
    catch (e) { onError(e.response?.data?.detail || 'Could not revoke the key.'); }
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Keys authenticate scripts and other systems. Send one as the
          <code> X-API-Key</code> header.
        </Typography>
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
          New key
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key ID</TableCell>
              <TableCell>Scope</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last used</TableCell>
              <TableCell>Uses</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No API keys yet.
                </Typography>
              </TableCell></TableRow>
            ) : rows.map((k) => (
              <TableRow key={k.id}>
                <TableCell>{k.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>
                  {k.public_id}
                </TableCell>
                <TableCell>
                  <Chip size="small" label={k.scope}
                        color={k.scope === 'read' ? 'default' : 'warning'} />
                </TableCell>
                <TableCell>{k.agent || 'any'}</TableCell>
                <TableCell>
                  <Chip size="small" label={k.status}
                        color={k.status === 'active' ? 'success' : 'default'} />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(k.last_used_at)}</TableCell>
                <TableCell>{k.use_count}</TableCell>
                <TableCell align="right">
                  {k.status === 'active' && (
                    <Tooltip title="Revoke">
                      <IconButton size="small" color="warning" onClick={() => revoke(k.id)}>
                        <BlockIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New API key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" fullWidth value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       helperText="What uses it. This is how you will recognise it later." />
            <FormControl fullWidth>
              <InputLabel id="scope">Scope</InputLabel>
              <Select labelId="scope" label="Scope" value={form.scope}
                      onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                <MenuItem value="read">read — cannot change anything</MenuItem>
                <MenuItem value="write">write — can change the directory</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="agent">Domain controller</InputLabel>
              <Select labelId="agent" label="Domain controller" value={form.agent}
                      onChange={(e) => setForm({ ...form, agent: e.target.value })}>
                <MenuItem value="">any — the caller chooses</MenuItem>
                {agents.map((a) => (
                  <MenuItem key={a.name} value={a.name}>{a.name}</MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Pinning a key to one DC means it cannot be pointed at another,
                whatever header the caller sends.
              </Typography>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={create} disabled={busy || !form.name.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* The one and only time the secret is visible. */}
      <Dialog open={Boolean(issued)} onClose={() => setIssued(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Copy this key now</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>It cannot be shown again</AlertTitle>
            Only a hash is stored. If you lose it, revoke this key and issue another.
          </Alert>
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all', flexGrow: 1 }}>
              {issued?.key}
            </Box>
            <Tooltip title="Copy">
              <IconButton onClick={() => navigator.clipboard?.writeText(issued?.key || '')}>
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setIssued(null)}>I have copied it</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Webhooks({ agents, events, onError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', url: '', events: [], agent: '', active: true });
  const [secret, setSecret] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deliveries, setDeliveries] = useState(null);
  const [editing, setEditing] = useState(null);   // hook being edited, null = new
  const [hdrs, setHdrs] = useState([]);           // [{key, value, existing}]

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/integrations/webhooks/');
      setRows(r.data.webhooks || []);
    } catch (e) {
      onError(e.response?.data?.detail || 'Could not load webhooks.');
    } finally { setLoading(false); }
  }, [onError]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', url: '', events: [], agent: '', active: true });
    setHdrs([]);
    setOpen(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({ name: w.name, url: w.url, events: w.events || [],
              agent: w.agent || '', active: w.active });
    // Header VALUES are never returned by the API — they hold tokens. An
    // existing header therefore shows its name with an empty value: leave it
    // blank to keep what is stored, type to replace it. The dialog says so
    // rather than leaving it to be guessed.
    setHdrs((w.header_names || []).map((k) => ({ key: k, value: '', existing: true })));
    setOpen(true);
  };

  const headersPayload = () => {
    const out = {};
    hdrs.forEach(({ key, value, existing }) => {
      const k = (key || '').trim();
      if (!k) return;
      if (existing && !value) return;   // untouched: keep the stored value
      out[k] = value;
    });
    // A row removed from the list means "delete that header".
    (editing?.header_names || []).forEach((k) => {
      if (!hdrs.some((h) => (h.key || '').trim() === k)) out[k] = null;
    });
    return out;
  };

  const save = async () => {
    setBusy(true);
    try {
      const body = { name: form.name, url: form.url, events: form.events,
                     active: form.active, headers: headersPayload() };
      if (form.agent) body.agent = form.agent;
      if (editing) {
        await api.patch(`/integrations/webhooks/${editing.id}/`, body);
      } else {
        const r = await api.post('/integrations/webhooks/', body);
        if (r.data?.secret) setSecret(r.data.secret);
      }
      setOpen(false); setEditing(null); setHdrs([]);
      setForm({ name: '', url: '', events: [], agent: '', active: true });
      load();
    } catch (e) {
      const d = e.response?.data;
      onError(d?.detail || (d ? JSON.stringify(d) : 'Could not save the webhook.'));
    } finally { setBusy(false); }
  };

  const test = async (id) => {
    try {
      const r = await api.post(`/integrations/webhooks/${id}/test/`);
      onError(`Test delivery: ${r.data?.status || r.data?.detail || 'sent'}`, 'info');
      load();
    } catch (e) { onError(e.response?.data?.detail || 'Test delivery failed.'); }
  };

  const remove = async (id) => {
    try { await api.delete(`/integrations/webhooks/${id}/`); load(); }
    catch (e) { onError(e.response?.data?.detail || 'Could not delete the webhook.'); }
  };

  const showDeliveries = async (hook) => {
    try {
      const r = await api.get(`/integrations/webhooks/${hook.id}/deliveries/`);
      setDeliveries({ hook, items: r.data.deliveries || r.data.results || [] });
    } catch (e) { onError(e.response?.data?.detail || 'Could not load deliveries.'); }
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          Fired when the directory changes, so another system does not have to
          poll. Each delivery is signed — verify the signature before trusting it.
        </Typography>
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          New webhook
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 900 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Events</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Last attempt</TableCell>
              <TableCell>Failures</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No webhooks yet.
                </Typography>
              </TableCell></TableRow>
            ) : rows.map((w) => (
              <TableRow key={w.id}>
                <TableCell>{w.name}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all', maxWidth: 260 }}>
                  {w.url}
                </TableCell>
                <TableCell sx={{ maxWidth: 260 }}>
                  {(w.events && w.events.length) ? (
                    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {w.events.map((ev) => (
                        <Chip key={ev} size="small" variant="outlined" label={ev} />
                      ))}
                    </Stack>
                  ) : <Chip size="small" label="all events" />}
                  {(w.header_names || []).length > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      auth: {w.header_names.join(', ')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{w.agent || 'any'}</TableCell>
                <TableCell>
                  <Chip size="small" label={w.active ? 'active' : 'off'}
                        color={w.active ? 'success' : 'default'} />
                  {w.disabled_reason && (
                    <Typography variant="caption" color="error" display="block">
                      {w.disabled_reason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {fmt(w.last_attempt_at)}
                  {w.last_status ? ` (${w.last_status})` : ''}
                </TableCell>
                <TableCell>{w.consecutive_failures}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(w)}><EditIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Send a test delivery">
                    <IconButton size="small" onClick={() => test(w.id)}><SendIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Recent deliveries">
                    <IconButton size="small" onClick={() => showDeliveries(w)}><HistoryIcon /></IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => remove(w.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'New webhook'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" fullWidth value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label="URL" fullWidth value={form.url}
                       onChange={(e) => setForm({ ...form, url: e.target.value })}
                       helperText="http:// or https:// only. Redirects are never followed." />

            <FormControl fullWidth>
              <InputLabel id="events">Events</InputLabel>
              <Select
                labelId="events" label="Events" multiple value={form.events}
                onChange={(e) => setForm({ ...form, events: e.target.value })}
                MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                renderValue={(sel) => (
                  sel.length === 0 ? 'all events' : (
                    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
                      {sel.map((v) => <Chip key={v} size="small" label={v} />)}
                    </Stack>
                  )
                )}
              >
                {events.map((ev) => (
                  <MenuItem key={ev} value={ev} dense>
                    <Checkbox size="small" checked={form.events.indexOf(ev) > -1} />
                    <ListItemText primary={ev} />
                  </MenuItem>
                ))}
              </Select>
              <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                <Button size="small" onClick={() => setForm({ ...form, events: [...events] })}>
                  Select all
                </Button>
                <Button size="small" onClick={() => setForm({ ...form, events: [] })}>
                  Clear (= all events)
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Selecting none means every event, including ones added in future
                releases. Selecting all pins the list to today's events.
              </Typography>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="whagent">Domain controller</InputLabel>
              <Select labelId="whagent" label="Domain controller" value={form.agent}
                      onChange={(e) => setForm({ ...form, agent: e.target.value })}>
                <MenuItem value="">any</MenuItem>
                {agents.map((a) => <MenuItem key={a.name} value={a.name}>{a.name}</MenuItem>)}
              </Select>
            </FormControl>

            <Divider textAlign="left">
              <Typography variant="caption">Authentication (optional)</Typography>
            </Divider>
            <Typography variant="caption" color="text.secondary">
              Extra headers sent with every delivery — an <code>Authorization</code>
              header, an API token, whatever the receiver requires. The signature
              proves the body came from us; these are for receivers that also want
              credentials of their own.
              {editing && ' Existing values are not shown: leave one blank to keep it, type to replace it.'}
            </Typography>
            {hdrs.map((h, idx) => (
              <Stack key={idx} direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="Header" value={h.key} sx={{ flex: 1 }}
                           onChange={(e) => setHdrs(hdrs.map((x, n) =>
                             n === idx ? { ...x, key: e.target.value } : x))} />
                <TextField size="small" label={h.existing ? 'New value (blank = keep)' : 'Value'}
                           type="password" value={h.value} sx={{ flex: 1.4 }}
                           onChange={(e) => setHdrs(hdrs.map((x, n) =>
                             n === idx ? { ...x, value: e.target.value } : x))} />
                <IconButton size="small" color="error"
                            onClick={() => setHdrs(hdrs.filter((x, n) => n !== idx))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button size="small" startIcon={<AddIcon />}
                    onClick={() => setHdrs([...hdrs, { key: '', value: '' }])}>
              Add header
            </Button>

            <FormControlLabel
              control={<Switch checked={form.active}
                               onChange={(e) => setForm({ ...form, active: e.target.checked })} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save}
                  disabled={busy || !form.name.trim() || !form.url.trim()}>
            {editing ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(secret)} onClose={() => setSecret(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Signing secret</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Shown once. The receiving system needs it to verify that a delivery
            really came from here — without that check, anyone who learns the URL
            can post to it.
          </Alert>
          <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', wordBreak: 'break-all', flexGrow: 1 }}>
              {secret}
            </Box>
            <IconButton onClick={() => navigator.clipboard?.writeText(secret || '')}>
              <ContentCopyIcon />
            </IconButton>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setSecret(null)}>I have copied it</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deliveries)} onClose={() => setDeliveries(null)} maxWidth="md" fullWidth>
        <DialogTitle>Recent deliveries — {deliveries?.hook?.name}</DialogTitle>
        <DialogContent>
          {(!deliveries?.items || deliveries.items.length === 0) ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Nothing delivered yet.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deliveries.items.map((d, i) => (
                  <TableRow key={d.id || i}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(d.created_at)}</TableCell>
                    <TableCell>{d.event}</TableCell>
                    <TableCell>
                      <Chip size="small" label={d.status_code || d.status || '—'}
                            color={String(d.status_code).startsWith('2') ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.error || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDeliveries(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

function Integrations() {
  const user = useSelector((state) => state.auth.user);
  const isAdmin = Boolean(user?.is_staff || user?.is_superuser);

  const [tab, setTab] = useState(0);
  const [agents, setAgents] = useState([]);
  const [events, setEvents] = useState([]);
  const [msg, setMsg] = useState(null);

  const notify = useCallback((text, severity = 'error') => setMsg({ text, severity }), []);

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const a = await api.get('/agents/');
        setAgents(a.data.agents || a.data.results || []);
      } catch { /* the pin dropdown just offers "any" */ }
      try {
        const e = await api.get('/integrations/events/');
        setEvents(e.data.events || []);
      } catch { /* selecting none means all, so this is survivable */ }
    })();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <Alert severity="warning" sx={{ maxWidth: 640 }}>
        <AlertTitle>Administrators only</AlertTitle>
        API keys and webhooks grant access to the directory, so only
        administrators can see or manage them.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Integrations</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Let other systems read from and react to this directory.
      </Typography>

      {msg && (
        <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>
          {msg.text}
        </Alert>
      )}

      <Card variant="outlined">
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ px: 1 }}>
          <Tab label="API keys" />
          <Tab label="Webhooks" />
        </Tabs>
        <Divider />
        <CardContent>
          {tab === 0 && <ApiKeys agents={agents} onError={notify} />}
          {tab === 1 && <Webhooks agents={agents} events={events} onError={notify} />}
        </CardContent>
      </Card>
    </Box>
  );
}

export default Integrations;
